import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AccountActions } from '@app/core/actions';
import { DomainService } from '@app/core/services/domain.service';
import { IamService } from '@app/core/services/iam.service';
import { TokenService } from '@app/core/services/token.service';
import * as fromRoot from '@app/reducers';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Domain, errors } from '@smash-sdk/domain/ejs/01-2024';
import { from, of } from 'rxjs';
import { catchError, concatMap, tap, withLatestFrom } from 'rxjs/operators';
import { Constant } from 'src/constant';
import * as fromSignup from '../../reducers';
import { DomainActionsSignup } from '../actions';



@Injectable()
export class DomainEffects {

  createDomain$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DomainActionsSignup.createDomain),
      withLatestFrom(
        this.store$.select(fromSignup.getDomainToCreate),
      ),
      concatMap(([_, domain]: any[]) => {
        const domainSdk = new Domain();
        return from(domainSdk.createDomain({ domain, options: Constant.defaultPremiumDomainSettings })).pipe(
          concatMap(({ domain }) => {
            return [
              DomainActionsSignup.createDomainSuccess({ domain }),
              DomainActionsSignup.InitiateTeam({ domain }),
            ];
          }),
          catchError((error: HttpErrorResponse) => {
            if (error instanceof errors.CreateDomainError.DomainAlreadyExistError) {
              return of(DomainActionsSignup.createDomainFailure({ error: 'DOMAIN_ALREADY_EXISTS' }));
            } else {
              return of(DomainActionsSignup.createDomainFailure({ error: error.message }));
            }
          })
        );
      })
    )
  );

  initiateTeam$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DomainActionsSignup.InitiateTeam),
      withLatestFrom(
        this.store$.select(fromRoot.getPlan),
        this.store$.select(fromRoot.getActiveIdentity)
      ),
      concatMap(([action, plan, identity]: any[]) => {
        const { team, region, domain } = action.domain;
        const hydratedTeam = { id: team, domain };
        const teamName = domain.split('.').shift();
        const account = this.tokenService.getAccountFromToken(identity.token.token);
        return this.iamService.setupTeamRoles({ account, team: hydratedTeam, region }).pipe(concatMap(res => {
          return this.iamService.updateTeam(team, { name: teamName }).pipe(
            concatMap((resTeam) => {
              return [
                AccountActions.selectActiveTeam({ team: resTeam.team }),
                DomainActionsSignup.InitiateTeamSuccess({ team: resTeam.team }),
              ];
            }),
            catchError((error: HttpErrorResponse) => {
              if (error.status === 409) {
                return of(DomainActionsSignup.InitiateTeamFailure({ error: error.message }));
              } else {
                return of(DomainActionsSignup.InitiateTeamFailure({ error: error.message }));
              }
            })
          );
        }));
      })
    )
  );

  initiateTeamSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DomainActionsSignup.InitiateTeamSuccess),
      withLatestFrom(
        this.store$.select(fromRoot.getDevice),
      ),
      tap(([action, device]) => {
        if (device.isMobile) {
          this.router.navigateByUrl('/account/team/' + action.team.id + '/transfers/sent');
        } else {
          this.router.navigateByUrl('/account/team/' + action.team.id + '/transfers/sent');
        }
      })
    ),
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private domainService: DomainService,
    private iamService: IamService,
    private tokenService: TokenService,
    private store$: Store<fromRoot.State & fromSignup.State>,
    private readonly router: Router
  ) { }

}
